import React, { FC } from 'react'
import { styled } from '@mui/material/styles'
import cn from 'classnames'
import { useStore } from 'store'
import { observer } from 'mobx-react'
import Button from '@mui/material/Button'
import { cloudinary } from 'utils'
import { SxProps } from '@mui/system'

import { ReactComponent as OpenseaIcon } from 'assets/images/opensea.svg'
import { ReactComponent as UniswapIcon } from 'assets/images/uniswap.svg'
import { ReactComponent as GeminiIcon } from 'assets/images/gemini.svg'
import { ReactComponent as EconomyIcon } from 'assets/images/economy.svg'
import { ReactComponent as PDFIcon } from 'assets/images/pdf_red.svg'

const PREFIX = 'ExploreButton'

const classes = {
  button: `${PREFIX}-button`,
  disabled: `${PREFIX}-disabled`,
  opensea: `${PREFIX}-opensea`,
  uniswap: `${PREFIX}-uniswap`,
  gemini: `${PREFIX}-gemini`,
  economy: `${PREFIX}-economy`
}

const getButtonInfo = (type: TExploreButtonType) => {
  switch (type) {
    case 'opensea':
      return ({
        element: <><OpenseaIcon/>Opensea Marketplace</>,
        link: 'https://opensea.io/'
      })
    case 'uniswap':
      return ({
        element: <><UniswapIcon/>Uniswap Exchange</>,
        link: 'https://uniswap.org/'
      })
    case 'gemini':
      return ({
        element: <><GeminiIcon/>Gemini Exchange</>,
        link: 'https://www.gemini.com/'
      })
    case 'economy':
      return ({
        element: <><EconomyIcon/>Economy Paper<PDFIcon
          style={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}/></>,
        link: 'https://www.gemini.com/'
      })
  }
}

const getLink = (type: TExploreButtonType) => {
  switch (type) {
    case 'opensea':
      return (<><OpenseaIcon/>Opensea Marketplace</>)
    case 'uniswap':
      return (<><UniswapIcon/>Uniswap Exchange</>)
    case 'gemini':
      return (<><GeminiIcon/>Gemini Exchange</>)
    case 'economy':
      return (<><EconomyIcon/>Economy Paper<PDFIcon style={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}/></>)
  }
}

const StyledButton = styled(Button)({
  [`&.${classes.button}`]: {
    height: '80px',
    width: '100px',
    color: '#FFF',
    fontFamily: 'Exo, sans-serif',
    fontSize: '0.875rem',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '120%',
    textTransform: 'none',
    borderRadius: '5px',
    padding: '5px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    textAlign: 'start',
    position: 'relative',

    'svg': {
      marginBottom: '3px',
      minHeight: '30px'
    }
  },
  [`&.${classes.disabled}`]: {
    opacity: 0.5
  },
  [`&.${classes.opensea}`]: {
    border: '2px solid #47A7EB',
    background: '#0A3A5C',

    '&:hover': {
      background: '#1474B8'
    },
    '&:active': {
      background: '#47A7EB',
      border: '2px solid #FFF'
    }
  },
  [`&.${classes.uniswap}`]: {
    border: '2px solid #F6F',
    background: '#909',

    '&:hover': {
      background: '#F0F'
    },
    '&:active': {
      background: '#F6F',
      border: '2px solid #FFF'
    }
  },
  [`&.${classes.gemini}`]: {
    border: '2px solid #66E0FF',
    background: '#007A99',

    '&:hover': {
      background: '#0CF'
    },
    '&:active': {
      background: '#66E0FF',
      border: '2px solid #FFF'
    }
  },
  [`&.${classes.economy}`]: {
    border: '2px solid #8F90A6',
    background: '#131B29',

    '&:hover': {
      background: '#323641'
    },
    '&:active': {
      background: '#555770',
      border: '2px solid #FFF'
    }
  }
})

type TExploreButtonType = 'opensea' | 'uniswap' | 'gemini' | 'economy'

interface IProps {
  styleType: TExploreButtonType,
  sx?: SxProps,
  type?: 'submit' | 'reset' | 'button',
  disabled?: boolean
}

export const ExploreButton: FC<IProps> = (
  {
    sx,
    type,
    styleType,
    disabled
  }) => {
  const buttonInfo = getButtonInfo(styleType)

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    window.open(buttonInfo.link, '_blank', 'noopener,noreferrer')
  }

  return (
    <StyledButton
      type={type}
      variant={'outlined'}
      disabled={disabled}
      className={cn({
        [classes.button]: true,
        [classes.disabled]: disabled,
        [classes.opensea]: styleType === 'opensea',
        [classes.uniswap]: styleType === 'uniswap',
        [classes.gemini]: styleType === 'gemini',
        [classes.economy]: styleType === 'economy'
      })}
      sx={sx}
      onClick={handleOnClick}
    >
      {buttonInfo.element}
    </StyledButton>
  )
}
